import { init, reactRouterV6BrowserTracingIntegration, replayIntegration } from "@sentry/react";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import App from "./app/app";
import { captureAndStripOAuthParams, scrubSentryEvent } from "./app/utils/telemetrySecurity";

const initiateSentry = () => {
  init({
    dsn: "https://68134b9672b395646a64189e175c42cb@o4509399107764225.ingest.us.sentry.io/4509440515637248",
    environment: process.env.NX_PUBLIC_SENTRY_ENVIRONMENT,
    // Do not attach default PII (IP, cookies, headers) to captured events.
    sendDefaultPii: false,
    // Strip OAuth secrets (?code=/state/session_state) from any captured event
    // URL / navigation breadcrumb before it leaves the browser (CWE-598).
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    // release: process.env.NX_PUBLIC_SENTRY_RELEASE_VERSION,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      reactRouterV6BrowserTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Mask text/inputs and block media in session replay so credentials shown
      // on screen are not recorded.
      replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,
    autoSessionTracking: true,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
};

// Strip the OAuth code from the URL BEFORE Sentry initializes: Session Replay starts
// recording (and captures the pageload URL) at init() and uploads on a separate
// envelope that bypasses beforeSend, so the code must be gone from the URL first.
// The code is stashed in memory for the login flow to consume (see RootRouter).
captureAndStripOAuthParams();

if (process.env.NX_PUBLIC_SENTRY_ENABLED === "true") {
  // eslint-disable-next-line no-console
  console.log("Sentry enabled", process.env.NX_PUBLIC_SENTRY_ENABLED);
  initiateSentry();
}

const root = createRoot(document.getElementById("root") as HTMLElement);
root.render(<App />);
